
import { trpc } from '@tencent/tpro_rmonitor_web_server_exception';
// import { Modal } from 'antd';
import { Modal } from 'antd';
import Decimal from 'decimal.js';
import { pickBy, isNil } from 'lodash-es';
import moment, { Moment } from 'moment/moment';
import i18n from '@/i18n';
import { isAndroid, isAppleTV, isFlutter, isHarmonyOS, isIOS } from '@/infrastructure/product-info';
import { formatNum } from '@/infrastructure/utils/digit';
import {
  CommonDateRange, DATE_TIME_FORMAT,
  getDateRangeByShortCut,
  getTodayTimeRange,
} from '@/infrastructure/utils/timestamp';
import { CUSTOM, shortRanges } from '@/presentation/components/form/components/time-range-cascade';
import { SearchValueStorageType } from '@/presentation/config/type';
import { IssueTimeType } from '@/proto/issue';
import { ExceptionType, TimeType } from '@/proto/metric';
import { WithTypeCast } from '@raft/utils';
type IAttachment = WithTypeCast<trpc.rmonitor.web_server.IAttachment>;

export function getIssueDetailUrl(exceptionType: string) {
  let type = '';
  if (exceptionType === 'crash' || exceptionType === 'crashes') {
    type = 'crash';
  }
  if (exceptionType === 'error' || exceptionType === 'errors') {
    type = 'error';
  }
  if ([ExceptionType.Freeze, 'blocks', 'anr'].includes(exceptionType)) {
    type = 'anr';
  }
  if (exceptionType === 'foom') {
    type = 'foom';
  }
  if (exceptionType === 'electron-crash') {
    type = 'electron-crash';
  }

  return `/exception/${type}/issues/detail`;
}

export const ISSUE_STATUS_CHANGE_FROM_PAGE = {
  realtimeTopIssue: 'realtimeTopIssue',
  dailyTopIssue: 'dailyTopIssue',
  issueList: 'issueList',
  issueDetail: 'issueDetail',
};

export function transformMemData(data: string) {
  const parseIntData = parseInt(data, 10);
  const signData = Math.sign(parseIntData);
  const isValid = !isNaN(signData) && signData !== 0 && signData !== -1;
  if (!isValid) {
    return '-';
  }
  const fixedData = (data: number) => Number(data?.toFixed(2));
  const kbData = fixedData(parseIntData / 1024);
  if (kbData > 1048576) {
    const data = fixedData(kbData / 1048576);
    return `${formatNum(data)} GB`;
  } if (kbData > 1024) {
    const data = fixedData(kbData / 1024);
    return `${formatNum(data)} MB`;
  }
  return `${formatNum(kbData)} KB`;
}

export function getRate(free: number, total: number) {
  if (total > 0 && free >= 0) {
    return ((100 * free) / total).toFixed(2);
  }
  return 0;
}

export function getPageTraceContent(attachList: IAttachment[]) {
  if (!attachList) {
    return '';
  }
  let contents = '';
  attachList.forEach((item) => {
    if (item.name === 'martianlog.txt') {
      contents += item.content;
    }
  });
  return contents;
}


export function transformApn(apn: string, isAndroid: boolean) {
  if (!apn) {
    return i18n.t('未知');
  }

  if (isAndroid && /^MOBILE/.test(apn)) {
    return i18n.t('移动网络');
  }

  if (!isAndroid) {
    switch (apn) {
      case 'XG':
        return i18n.t('移动网络');
      case 'iphonesimulator':
        return i18n.t('iPhone模拟器');
      case 'iphonesimulator_notreach':
        return i18n.t('iPhone模拟器网络不可连接');
      case 'notreach':
        return i18n.t('网络不可连接');
      default:
        return apn;
    }
  }
  if (apn === 'WIFI') {
    return 'Wi-Fi';
  }
  return apn;
}


export const searchValueDeserializer = (value?: SearchValueStorageType) => value?.map?.((item) => {
  const dateRange = getDateRangeByShortCut(item?.values?.timeQuick ?? '', shortRanges);
  if (!Array.isArray(dateRange)  || dateRange?.length !== 2) {
    return item;
  }
  const compareDate: Record<string, any> = {};
  if (item?.values?.isCompareOpen) {
    const dateRangeCompare = getDateRangeByShortCut(item?.values?.timeQuickCompare ?? '', shortRanges);
    compareDate.startTimeCompare = moment(dateRangeCompare[0]).format(DATE_TIME_FORMAT);
    compareDate.endTimeCompare = moment(dateRangeCompare[1]).format(DATE_TIME_FORMAT);
  }
  const startTime = moment(dateRange[0]).unix()
    .toString();
  const endTime = moment(dateRange[1]).unix()
    .toString();
  return { name: item.name, id: item.id, values: { ...item?.values, startTime, endTime, ...compareDate } };
}) || [];

export const initJudgmentTimeRange = (
  initialValue: Record<string, any>, isAnalytics?: boolean,
  callback?: () => void,
): boolean => {
  if (Array.isArray(initialValue.timeRange) && initialValue.timeRange.length === 2) {
    return judgmentTime(
      moment(initialValue.timeRange[0]),
      moment(initialValue.timeRange[1]),
      initialValue.timeWindow,
      isAnalytics,
      true,
      callback,
    );
  }

  return false;
};

export const judgmentTime = (
  startDate: Moment,
  endDate: Moment,
  timeWindow: number | undefined,
  isAnalytics?: boolean,
  autoModal?: boolean,
  callback?: () => void,
): boolean => {
  // 时间范围是否过期
  let expired = false;
  if (TimeType.HOUR === timeWindow || TimeType.MINUTE5 === timeWindow) {
    const timeRange = CommonDateRange.lastDaysFull(14);
    const timeRangeStart = timeRange[0].startOf('date').unix();
    const timeRangeEnd = timeRange[1].endOf('date').unix();
    const startDateDiff = startDate.unix() >= timeRangeStart;
    const endDateDiff = endDate.unix() <= timeRangeEnd;

    expired = !((startDateDiff && endDateDiff));
  } else if (timeWindow === TimeType.DAY) {
    const timeRange = isAnalytics ? CommonDateRange.lastDaysFull(180) : CommonDateRange.lastDaysFull(90);
    const timeRangeStart = timeRange[0].startOf('date').unix();
    const timeRangeEnd = timeRange[1].endOf('date').unix();
    const startDateDiff = startDate.unix() >= timeRangeStart;
    const endDateDiff = endDate.unix() <= timeRangeEnd;

    expired = !((startDateDiff && endDateDiff));
  } else {
    const timeRange = CommonDateRange.lastDaysFull(90);
    const timeRangeStart = timeRange[0].startOf('date').unix();
    const timeRangeEnd = timeRange[1].endOf('date').unix();
    const startDateDiff = startDate.unix() >= timeRangeStart;
    const endDateDiff = endDate.unix() <= timeRangeEnd;
    expired = !((startDateDiff && endDateDiff));
  }

  if (autoModal && expired) {
    Modal.confirm({
      type: 'warn',
      content: i18n.t('时间范围超出数据有效性，是否调整时间范围？'),
      onOk: callback,
    });
  }

  return expired;
};

export function arrayMoveMutable(array: any[], fromIndex: number, toIndex: number) {
  const startIndex = fromIndex < 0 ? array.length + fromIndex : fromIndex;

  if (startIndex >= 0 && startIndex < array.length) {
    const endIndex = toIndex < 0 ? array.length + toIndex : toIndex;

    const [item] = array.splice(fromIndex, 1);
    array.splice(endIndex, 0, item);
  }
}


export const getIssueDefault = (timeRangeStateData?: { timeQuick?: string; timeWindow?: any; }) => {
  if (timeRangeStateData?.timeQuick && timeRangeStateData.timeWindow) {
    let dateRange = getDateRangeByShortCut(timeRangeStateData?.timeQuick ?? '', shortRanges);
    if (timeRangeStateData?.timeQuick === CUSTOM
      && [IssueTimeType.TIME_TYPE_MINUTE5, IssueTimeType.TIME_TYPE_HOUR].includes(timeRangeStateData?.timeWindow)) {
      dateRange = getDateRangeByShortCut('今天', shortRanges);
    }
    if (timeRangeStateData?.timeQuick === CUSTOM
      && TimeType.WEEK === timeRangeStateData?.timeWindow) {
      dateRange = getDateRangeByShortCut('近30天', shortRanges);
    }
    if (timeRangeStateData?.timeQuick === CUSTOM
      && TimeType.MONTH === timeRangeStateData?.timeWindow) {
      dateRange = getDateRangeByShortCut('近90天', shortRanges);
    }
    if (!Array.isArray(dateRange) || dateRange?.length !== 2) {
      return {
        ...getTodayTimeRange(),
        timeWindow: IssueTimeType.TIME_TYPE_MINUTE5,
      };
    }

    return {
      timeWindow: timeRangeStateData.timeWindow,
      timeQuick: timeRangeStateData?.timeQuick,
      startTime: moment(dateRange[0]).unix()
        .toString(),
      endTime: moment(dateRange[1]).unix()
        .toString(),
    };
  }
  return {
    ...getTodayTimeRange(),
    timeWindow: IssueTimeType.TIME_TYPE_MINUTE5,
  };
};

export enum SortType {
  String='string',
  Number='number',
  Date='date'
}

export const sort = (type: SortType, aValue: string | number, bValue: string | number) => {
  if (type === SortType.String && typeof aValue === 'string' && typeof bValue === 'string') {
    return aValue.localeCompare(bValue);
  }
  if (type === SortType.Number && typeof aValue === 'number' && typeof bValue === 'number') {
    return aValue - bValue;
  }
  if (type === SortType.Date) {
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return aValue - bValue;
    }
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return  moment(aValue).unix() - moment(bValue).unix();
    }
  }
  return 0;
};


// token对象内的undefined和null属性删除
export const  deleteUndefinedOrNullProperties = (obj: Record<string, any>): any => pickBy(obj, (value) => {
  if (Array.isArray(value) && value.every(item => item === null || item === undefined)) {
    return false;
  }
  if (value?.value
      && Array.isArray(value.value)
    && value.value.every((item: any) => item === null || item === undefined)) {
    return false;
  }
  return (!isNil(value) && value !== '');
});

// 用来打开对比时同步时间
export const getCompareDateValue = (formValues: Record<string, any>, compareSwitch?: boolean) => {
  if (!compareSwitch) {
    return undefined;
  }
  return {
    accurateDateCompare: formValues.accurateDate,
    timeQuickCompare: formValues.timeQuick,
    timeRangeCompare: formValues.timeRange,
    timeTypeCompare: formValues.timeType,
    timeWindowCompare: formValues.timeWindow,
  };
};

export const calculatePercentage = (
  divisor?: number | string,
  dividend?: number | string,
  ratio = 100,
  decimal = 2,
) => {
  if (!divisor || !dividend) {
    return '0';
  }
  return new Decimal(divisor).div(dividend)
    .mul(ratio)
    .toDecimalPlaces(decimal, Decimal.ROUND_DOWN)
    .toString();
};

export const getTimeWindow = (stateTimeUnix: string | number, endTimeUnix: string | number) => {
  const endDate = moment.unix(Number(endTimeUnix));
  const stateDate = moment.unix(Number(stateTimeUnix));

  if (!endTimeUnix || !stateTimeUnix || endDate.diff(stateDate, 'days') > 1) {
    return IssueTimeType.TIME_TYPE_DAY;
  }
  return IssueTimeType.TIME_TYPE_MINUTE5;
};

// 支持数据标记的平台
export const isDataLabelingPlatform = () => (isAndroid() && !isFlutter()) || isIOS() || isAppleTV() || isHarmonyOS();
